import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { Button, Card, ErrorBox, Field, Input, Select, Spinner, Textarea } from "./kd";
import { reloadBausteine } from "./Snippets";

export function NotifyBell({ profil }: { profil: string }) {
  const [d, setD] = useState<any>({ items: [], ungelesen: 0 });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const box = useRef<HTMLDivElement>(null);
  const load = useCallback(() => { api.get("/benachrichtigungen").then((r) => setD(r.data)).catch(() => undefined); }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const go = async (i: any) => { setOpen(false); await api.post("/benachrichtigungen/gelesen", { ids: [i.id] }).catch(() => undefined); load(); nav(i.link); };
  const all = async () => { await api.post("/benachrichtigungen/gelesen", { alle: true }); load(); };
  return (
    <div className="relative" ref={box}>
      <button onClick={() => { setOpen(!open); if (!open) load(); }} aria-label={`Benachrichtigungen, ${d.ungelesen} ungelesen`} aria-expanded={open}
        className="relative flex min-h-[44px] min-w-[40px] items-center justify-center rounded-lg hover:bg-bg" data-testid="notify-bell">
        <Bell className="h-5 w-5" aria-hidden />
        {d.ungelesen > 0 && <span className="absolute right-1 top-1 min-w-[20px] rounded-full bg-danger px-1 text-center text-xs font-semibold leading-5 text-white" data-testid="notify-count">{d.ungelesen > 99 ? "99+" : d.ungelesen}</span>}
      </button>
      {open && (
        <div className="kd-fade fixed left-2 right-2 top-16 z-40 rounded-xl border border-border bg-surface shadow-card sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[380px]" data-testid="notify-panel">
          <div className="flex items-center justify-between border-b border-border px-4 py-2">
            <span className="font-medium">Benachrichtigungen</span>
            <button onClick={all} className="flex min-h-[36px] items-center gap-1 text-sm text-primary" data-testid="notify-read-all"><CheckCheck className="h-4 w-4" aria-hidden />Alle gelesen</button>
          </div>
          <ul className="max-h-[60vh] overflow-y-auto">
            {d.items.length === 0 && <li className="px-4 py-6 text-center text-sm text-text-muted">Keine Benachrichtigungen.</li>}
            {d.items.map((i: any) => (
              <li key={i.id}>
                <button onClick={() => go(i)} className={`block w-full border-b border-border px-4 py-3 text-left transition-colors last:border-0 hover:bg-bg ${i.gelesen ? "" : "bg-primary-light/60"}`} data-testid={`notify-item-${i.id}`}>
                  <span className={`block ${i.gelesen ? "" : "font-medium"}`}>{i.titel}</span>
                  <span className="text-xs text-text-muted">{fmtDateTime(i.erstellt_am)}</span>
                </button>
              </li>
            ))}
          </ul>
          <Link to={profil} onClick={() => setOpen(false)} className="block border-t border-border px-4 py-2 text-sm" data-testid="notify-settings-link">Einstellungen</Link>
        </div>
      )}
    </div>
  );
}

const b64 = (s: string) => { const p = "=".repeat((4 - (s.length % 4)) % 4); const raw = atob((s + p).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from(raw, (c) => c.charCodeAt(0)); };
const EMAIL_OPT: [string, string][] = [["SOFORT", "E-Mail sofort"], ["TAEGLICH", "Tägliche Zusammenfassung (18:00 Uhr)"], ["AUS", "Keine E-Mails"]];

async function subscribePush(key: string) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) throw new Error("Ihr Browser unterstützt keine Push-Benachrichtigungen. Tipp: Konsildent als App installieren oder in einem eigenen Browserfenster öffnen.");
  if ((await Notification.requestPermission()) !== "granted") throw new Error("Benachrichtigungen wurden im Browser nicht erlaubt.");
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64(key) });
  await api.post("/push/abo", sub.toJSON());
}

export function NotifySettings() {
  const [p, setP] = useState<any>(null);
  const [dev, setDev] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/benachrichtigungen/einstellungen").then((r) => setP(r.data)).catch((e) => setErr(errMsg(e)));
    navigator.serviceWorker?.getRegistration().then((reg) => reg?.pushManager.getSubscription()).then((s) => setDev(!!s)).catch(() => undefined);
  }, []);
  if (err) return <ErrorBox testid="notify-settings-error">{err}</ErrorBox>;
  if (!p) return <Spinner />;
  const save = async (email: string, push: boolean) => {
    setBusy(true);
    try {
      if (push !== dev) {
        if (push) await subscribePush(p.vapid_public_key);
        else {
          const sub = await (await navigator.serviceWorker?.getRegistration())?.pushManager.getSubscription();
          await api.post("/push/abo/entfernen", { endpoint: sub?.endpoint || null });
          await sub?.unsubscribe();
        }
        setDev(push);
      }
      await api.put("/benachrichtigungen/einstellungen", { email, push });
      setP({ ...p, email, push }); toast.success("Benachrichtigungen gespeichert.");
    } catch (e: any) { toast.error(e?.response ? errMsg(e) : e.message); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="notify-settings">
      <h2 className="mb-1">Benachrichtigungen</h2>
      <p className="mb-4 text-sm text-text-muted">Für neue Fälle, Nachrichten, Berichte und Erinnerungen. Benachrichtigungen enthalten nie Patientendaten, nur die Fallnummer.</p>
      <Field label="E-Mail" htmlFor="nm"><Select id="nm" options={EMAIL_OPT} value={p.email} disabled={busy} onChange={(e) => save(e.target.value, p.push)} className="max-w-md" data-testid="notify-email-select" /></Field>
      <label className="flex min-h-[44px] items-center gap-3">
        <input type="checkbox" className="h-5 w-5 accent-[var(--kd-primary)]" checked={dev} disabled={busy} onChange={(e) => save(p.email, e.target.checked)} data-testid="notify-push-toggle" />
        <span>Push-Benachrichtigungen auf diesem Gerät{p.push_geraete ? ` (${p.push_geraete} Gerät${p.push_geraete === 1 ? "" : "e"} insgesamt)` : ""}</span>
      </label>
    </Card>
  );
}

const EMPTY = { titel: "", kuerzel: "", text: "" };

export function BausteinManager() {
  const [list, setList] = useState<any[] | null>(null);
  const [form, setForm] = useState<any>(EMPTY);
  const [edit, setEdit] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const load = () => api.get("/textbausteine").then((r) => { setList(r.data); reloadBausteine(); });
  useEffect(() => { load(); }, []);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr("");
    try { edit ? await api.put(`/textbausteine/${edit}`, form) : await api.post("/textbausteine", form); toast.success("Textbaustein gespeichert."); setForm(EMPTY); setEdit(null); load(); }
    catch (ex) { setErr(errMsg(ex)); }
  };
  const del = async (b: any) => { if (!window.confirm(`Textbaustein „${b.titel}“ löschen?`)) return; await api.delete(`/textbausteine/${b.id}`); load(); };
  return (
    <Card data-testid="baustein-manager">
      <h2 className="mb-1">Textbausteine</h2>
      <p className="mb-4 text-sm text-text-muted">Im Chat und im Bericht „/“ und das Kürzel tippen, z. B. „/roentgen“, dann Enter.</p>
      {!list ? <Spinner /> : (
        <ul className="mb-5 divide-y divide-border rounded-lg border border-border">
          {list.map((b) => (
            <li key={b.id} className="flex items-start gap-3 p-3" data-testid={`baustein-${b.kuerzel}`}>
              <div className="min-w-0 flex-1"><p className="font-medium">{b.titel} <span className="text-sm font-normal text-text-muted">/{b.kuerzel}</span></p><p className="text-sm text-text-muted">{b.text}</p></div>
              <Button variant="ghost" aria-label="Bearbeiten" onClick={() => { setEdit(b.id); setForm({ titel: b.titel, kuerzel: b.kuerzel, text: b.text }); }} data-testid={`baustein-edit-${b.kuerzel}`}><Pencil className="h-4 w-4" aria-hidden /></Button>
              <Button variant="ghost" aria-label="Löschen" onClick={() => del(b)} data-testid={`baustein-delete-${b.kuerzel}`}><Trash2 className="h-4 w-4" aria-hidden /></Button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={submit} className="rounded-lg bg-bg p-4">
        <h3 className="mb-3">{edit ? "Textbaustein bearbeiten" : "Neuer Textbaustein"}</h3>
        <ErrorBox testid="baustein-error">{err}</ErrorBox>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Titel" required htmlFor="bt"><Input id="bt" value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} data-testid="baustein-titel-input" /></Field>
          <Field label="Kürzel" required htmlFor="bk"><Input id="bk" placeholder="z. B. roentgen" value={form.kuerzel} onChange={(e) => setForm({ ...form, kuerzel: e.target.value })} data-testid="baustein-kuerzel-input" /></Field>
        </div>
        <Field label="Text" required htmlFor="bx"><Textarea id="bx" rows={3} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="baustein-text-input" /></Field>
        <div className="flex gap-2">
          <Button type="submit" data-testid="baustein-save"><Plus className="h-4 w-4" aria-hidden />{edit ? "Speichern" : "Hinzufügen"}</Button>
          {edit && <Button variant="ghost" onClick={() => { setEdit(null); setForm(EMPTY); }}>Abbrechen</Button>}
        </div>
      </form>
    </Card>
  );
}
